import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Input, Button, message, Spin, Card } from 'antd';
import { ReloadOutlined, CloseOutlined } from '@ant-design/icons';
import { useT } from '@/common/utils/translation';
import { getCaptcha } from '@/services/common';
import { generateHardwareKey } from '@/lib/utils';
import { extractToken, validateToken } from '@/lib/watermark';

const CaptchaModal = ({ 
  visible, 
  onOk, 
  onCancel, 
  title,
  triggerElement, // 触发按钮的DOM元素，用于定位
  refreshTrigger // 外部触发刷新的标识，当这个值变化时自动刷新验证码
}) => {
  const t = useT();
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaImage, setCaptchaImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // 添加错误状态
  const [hardwareKey, setHardwareKey] = useState('');
  const [isClosing, setIsClosing] = useState(false); // 控制关闭动画
  const [watermarkToken, setWatermarkToken] = useState(''); // 盲水印token
  const cardRef = useRef(null); // Card组件引用

  // 生成硬件key
  useEffect(() => {
    if (visible) {
      const key = generateHardwareKey();
      setHardwareKey(key);
      // 重置状态
      setCaptchaCode('');
      setCaptchaImage(null);
      setError(null);
      setIsClosing(false);
      setWatermarkToken(''); // 重置盲水印token
    } else {
      // 当visible变为false时，重置isClosing状态
      setIsClosing(false);
    }
  }, [visible]);

  // 获取图片验证码
  const fetchCaptcha = async () => {
    if (!hardwareKey) return;
    
    setLoading(true);
    setError(null); // 清除之前的错误
    setCaptchaImage(null); // 清除之前的图片
    setWatermarkToken(''); // 清除之前的token
    
    try {
      const response = await getCaptcha(hardwareKey);
      

      
      // 正常情况下应该返回图片blob
      if (response instanceof Blob) {
        const imageUrl = URL.createObjectURL(response);
        setCaptchaImage(imageUrl);
        setError(null); // 成功时清除错误
        
        // 提取盲水印token
        try {
          const token = await extractToken(response);
          if (validateToken(token)) {
            setWatermarkToken(token);
          } else {
            setWatermarkToken(''); // 清空无效token
          }
        } catch (tokenError) {

          setWatermarkToken(''); // 清空token
        }
      } else {

        const errorMsg = t('captcha_get_failed');
        setError(errorMsg);
        message.error(errorMsg);
      }
    } catch (err) {

      
      // 现在LoginRequest会正确传递后端错误信息
      let errorMessage = t('captcha_get_failed');
      
      if (err.data && err.data.message) {
        // 后端返回的具体错误信息
        errorMessage = err.data.message;
      } else if (err.response && err.response.data && err.response.data.message) {
        // 尝试从response.data中获取
        errorMessage = err.response.data.message;
      } else if (err.message && err.message !== 'Network Error') {
        // 使用错误消息，但排除通用的网络错误
        errorMessage = err.message;
      }
      
      setError(errorMessage); // 设置错误状态
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载验证码
  useEffect(() => {
    if (visible && hardwareKey) {
      fetchCaptcha();
    }
  }, [visible, hardwareKey]);

  // 监听外部刷新触发器
  useEffect(() => {
    if (visible && hardwareKey && refreshTrigger) {
      refreshCaptcha();
    }
  }, [refreshTrigger]);

  // 刷新验证码
  const refreshCaptcha = () => {
    setCaptchaCode('');
    setError(null); // 重置错误状态
    fetchCaptcha();
  };

  // 确认提交
  const handleOk = () => {
    if (!captchaCode.trim()) {
      message.error(t('captcha_input_required'));
      return;
    }
    
    // 传递数据给父组件，让父组件处理后续逻辑
    onOk({
      code: captchaCode,
      key: hardwareKey,
      token: watermarkToken // 添加盲水印token
    });
  };

  // 处理回车事件，阻止事件冒泡
  const handleEnterPress = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleOk();
  };

  // 阻止键盘事件冒泡到下层页面
  const handleKeyDown = (e) => {
    e.stopPropagation();
  };

  // 关闭弹窗时清理
  const handleCancel = () => {
    setIsClosing(true);
    // 播放退出动画后关闭
    setTimeout(() => {
      setCaptchaCode('');
      setCaptchaImage(null);
      setError(null);
      setIsClosing(false);
      if (captchaImage) {
        URL.revokeObjectURL(captchaImage);
      }
      onCancel();
    }, 150); // 与动画时长匹配
  };

  // 计算弹窗位置和动画起点
  const getPopupStyle = () => {
    // 始终居中显示，避免位置计算问题
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 10000,
      width: '360px',
      transformOrigin: 'center center',
    };
  };

  // 处理点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (visible && event.target.closest('.captcha-popup') === null) {
        handleCancel();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
      // 给弹窗设置焦点，确保能够捕获键盘事件
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.focus();
        }
      }, 100);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [visible]);

  if (!visible && !isClosing) return null;

  // 使用 Portal 将内容渲染到 body 下
  return ReactDOM.createPortal(
    <>
      {/* 半透明遮罩层 */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          zIndex: 9999,
          display: visible ? 'block' : 'none',
          animation: isClosing ? 'fadeOut 0.15s' : 'fadeIn 0.3s',
        }}
        onClick={handleCancel}
      />
      
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        
        @keyframes captchaScaleIn {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.3);
            box-shadow: 0 0 0 rgba(0, 0, 0, 0);
          }
          50% {
            opacity: 0.8;
            transform: translate(-50%, -50%) scale(1.02);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          80% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(0.98);
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
        }
        
        @keyframes captchaScaleOut {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.2);
            box-shadow: 0 0 0 rgba(0, 0, 0, 0);
          }
        }
        
        .captcha-popup .ant-card-head {
          min-height: 40px !important;
          padding: 8px 12px !important;
        }
        
        .captcha-popup .ant-card-head-title {
          padding: 0 !important;
        }
        
        .captcha-popup .ant-card-body {
          padding: 8px 16px 16px 16px !important;
        }
        
        .captcha-popup .ant-card-head {
          border-bottom: none !important;
        }
        
        .captcha-popup {
          z-index: 10010 !important;
        }
        
        /* 修改输入框和按钮获得焦点时的边框颜色为主题紫色 */
        .captcha-popup .ant-input:focus,
        .captcha-popup .ant-input-focused {
          border-color: #7860FE !important;
          box-shadow: 0 0 0 2px rgba(120, 96, 254, 0.2) !important;
        }
        
        .captcha-popup .ant-btn-primary {
          background-color: #7860FE !important;
          border-color: #7860FE !important;
        }
        
        .captcha-popup .ant-btn-primary:hover {
          background-color: rgba(120, 96, 254, 0.8) !important;
          border-color: rgba(120, 96, 254, 0.8) !important;
        }
      `}</style>
      
      <Card 
        ref={cardRef}
        className="captcha-popup"
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ marginLeft: '8px' }}>{title || t('captcha_modal_title')}</span>
            <Button 
              type="text" 
              icon={<CloseOutlined />} 
              onClick={handleCancel}
              size="small"
            />
          </div>
        }
        style={{
          ...getPopupStyle(),
          display: visible ? 'block' : 'none',
          animation: isClosing 
            ? 'captchaScaleOut 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
            : 'captchaScaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div style={{ textAlign: 'center', padding: '4px 0 12px 0' }}>
        {/* 图片验证码显示区域 */}
        <div style={{ marginBottom: '12px', display: 'inline-block' }}>
          {loading ? (
            <div style={{ 
              width: '248px', // 与其他元素对齐
              height: '80px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #d9d9d9',
              borderRadius: '4px'
            }}>
              <Spin size="small" />
              <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                {t('captcha_loading_text')}
              </span>
            </div>
          ) : error ? (
            <div style={{ 
              width: '248px', // 与输入框和图片+按钮区域对齐
              height: '80px', 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #ff4d4f',
              borderRadius: '4px',
              backgroundColor: '#fff2f0',
              padding: '8px'
            }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#ff4d4f', 
                marginBottom: '8px',
                textAlign: 'center',
                lineHeight: '1.2'
              }}>
                {error}
              </div>
              <Button 
                type="primary" 
                size="small"
                icon={<ReloadOutlined />}
                onClick={refreshCaptcha}
                style={{ 
                  fontSize: '12px',
                  width: '120px' // 错误状态下的按钮适中宽度
                }}
              >
                {t('captcha_retry')}
              </Button>
            </div>
          ) : captchaImage ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <img 
                src={captchaImage} 
                alt="captcha"
                style={{ 
                  width: '200px', 
                  height: '80px',
                  border: 'none', // 移除图片外框
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={refreshCaptcha}
              />
              <Button 
                type="text" 
                icon={<ReloadOutlined />} 
                onClick={refreshCaptcha}
                title={t('captcha_refresh_text')}
                style={{ 
                  color: '#7860FE',
                  fontSize: '18px',
                  width: '40px', // 固定刷新按钮宽度
                  minWidth: '40px'
                }}
              />
            </div>
          ) : (
            <div style={{ 
              width: '248px', // 与其他元素对齐
              height: '80px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #d9d9d9',
              borderRadius: '4px'
            }}>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={refreshCaptcha}
                style={{ 
                  fontSize: '14px',
                  width: '140px' // 初始状态按钮宽度
                }}
              >
                {t('captcha_get_code')}
              </Button>
            </div>
          )}
        </div>

        {/* 验证码输入框和确定按钮 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          justifyContent: 'center'
        }}>
          <Input
            placeholder={t('captcha_input_placeholder')}
            value={captchaCode}
            onChange={(e) => setCaptchaCode(e.target.value)}
            onPressEnter={handleEnterPress}
            maxLength={10}
            style={{ 
              width: '168px',
              textAlign: 'left' // 光标左对齐
            }}
            autoFocus
          />
          <Button 
            type="primary"
            onClick={handleOk}
            style={{ width: '72px' }}
          >
            {t('ok') || '确定'}
          </Button>
        </div>
        </div>
      </Card>
    </>,
    document.body
  );
};

export default CaptchaModal; 